// A view's `at` clause seeds the scrubber.
//
// `at now` / `at v3` / `at now..v3` is the view SAYING where it reads the model,
// so switching to it has to move the scrubber there — otherwise a `Current` and
// a `ToBe` view over the same board paint identically and the clause is decor.
//
// Seeding is session-ephemeral by design: it fires when the ACTIVE VIEW changes,
// then leaves the scrubber alone, so dragging it afterwards is not fought by a
// re-seed on the next render. Persisting a live position back into the text goes
// through the ordinary knob-persist contract (VIEWS §9), not through here.

import { useEffect, useRef } from "react";
import type { ScrubPoint } from "@archlang/engine";
import { resetScrub, setComparePoint, setScrubPoint } from "./lifecycleScrubStore.js";

/** The view's declared point pair — structurally the engine's `AtClause` and
 *  the scene plan's `ScrubSpec`, which are the same shape. */
export interface ViewScrubSpec {
  readonly at: ScrubPoint;
  readonly compare?: ScrubPoint;
}

export function useViewScrubSeed(
  viewName: string | null | undefined,
  at: ViewScrubSpec | undefined,
): void {
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const key = viewName ?? "";
    if (seededFor.current === key) return;
    seededFor.current = key;
    // A view with no `at` is unfiltered authoring mode — park rather than
    // carrying the previous view's point into it.
    if (!at) {
      resetScrub();
      return;
    }
    setScrubPoint(at.at);
    setComparePoint(at.compare ?? null);
  }, [viewName, at]);
}
